/**
 * Haptic Feedback Utility
 * Provides vibration patterns for mobile interactions
 * Uses the Vibration API (https://developer.mozilla.org/en-US/docs/Web/API/Vibration_API)
 */

// Check if Vibration API is supported
const isSupported = typeof navigator !== 'undefined' && 'vibrate' in navigator;

/**
 * Trigger a vibration pattern
 * @param {number|number[]} pattern - Duration or array of durations [vibrate, pause, vibrate, ...]
 */
function vibrate(pattern) {
  if (isSupported) {
    try {
      navigator.vibrate(pattern);
    } catch (e) {
      // Silently fail - vibration is a nice-to-have
    }
  }
}

/**
 * Haptic feedback patterns for different interactions
 */
export const haptic = {
  /**
   * Light tap - for button presses, selections
   */
  tap: () => vibrate(8),

  /**
   * Medium feedback - for toggles, switches
   */
  medium: () => vibrate(15),

  /**
   * Heavy feedback - for important actions
   */
  heavy: () => vibrate(25),

  /**
   * Success pattern - for completed actions
   */
  success: () => vibrate([10, 50, 20]),

  /**
   * Error pattern - for failed actions
   */
  error: () => vibrate([30, 30, 30]),

  /**
   * Warning pattern - for alerts
   */
  warning: () => vibrate([20, 40, 20]),

  /**
   * Swipe gesture feedback
   */
  swipe: () => vibrate(5),

  /**
   * Task completion - satisfying pattern
   */
  taskComplete: () => vibrate([20, 50, 30, 50, 40]),

  /**
   * Badge unlock - celebratory pattern
   */
  badgeUnlock: () => vibrate([30, 80, 20, 80, 30, 80, 50]),

  /**
   * Level up - epic pattern
   */
  levelUp: () => vibrate([50, 100, 50, 100, 50, 100, 100]),

  /**
   * Focus mode start
   */
  focusStart: () => vibrate([30, 50, 30]),

  /**
   * Focus mode end - accomplished feeling
   */
  focusEnd: () => vibrate([20, 60, 30, 60, 40, 60, 50]),

  /**
   * Streak milestone
   */
  streak: () => vibrate([40, 80, 40, 80, 60]),

  /**
   * Notification received
   */
  notification: () => vibrate([10, 30, 10]),

  /**
   * Crisis mode trigger - attention pattern
   */
  crisis: () => vibrate([50, 50, 50, 50, 100]),

  /**
   * Mood check-in - gentle pattern
   */
  moodCheckIn: () => vibrate([15, 40, 15]),
};

/**
 * Hook for easy access to haptic feedback
 * @returns {object} Haptic feedback methods
 */
export function useHapticFeedback() {
  return {
    ...haptic,
    isSupported,
  };
}

export default haptic;
